// Missions service - API integration with localStorage cache

import api from '../utils/api.js';
import { missionsData } from '../data/missions-data.js';

const STORAGE_KEY = 'missions:state';

// Default state structure
const defaultState = {
  activeRun: null, // { missionId, startedAt, endsAt, runId }
  completedRuns: [], // Array of completed mission runs
  availableMissions: [], // Cached available missions for current city
  lastUpdateCity: null
};

class MissionsService {
  constructor() {
    this.state = this.loadState();
  }

  // Load state from localStorage
  loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate structure
        return {
          ...defaultState,
          ...parsed
        };
      }
    } catch (error) {
      console.error('Failed to load missions state:', error);
    }
    return { ...defaultState };
  }

  // Save state to localStorage
  saveState(suppressEvent = false) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      // Emit global event for state synchronization (unless suppressed)
      if (!suppressEvent) {
        this.emitStateChanged();
      }
    } catch (error) {
      console.error('Failed to save missions state:', error);
    }
  }

  // Emit global state change event
  emitStateChanged(source = 'service') {
    try {
      // Add debouncing to prevent rapid successive events
      if (this._emitTimeout) {
        clearTimeout(this._emitTimeout);
      }
      
      this._emitTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('missions:state-changed', {
          detail: {
            activeRun: this.state.activeRun,
            completedRuns: this.state.completedRuns.length,
            timestamp: Date.now(),
            source: source // Identify event source to prevent loops
          }
        }));
      }, 50); // 50ms debounce
    } catch (error) {
      console.warn('Failed to emit missions state change event:', error);
    }
  }

  // Get missions available for current city
  async list() {
    try {
      const response = await api.getMissions();
      const missions = response.missions || [];
      
      // Cache for quick access
      this.state.availableMissions = missions;
      this.saveState();

      return missions;
    } catch (error) {
      console.error('Failed to fetch missions:', error);
      
      // Return cached missions as fallback
      if (this.state.availableMissions.length > 0) {
        console.warn('Using cached missions data');
        return this.state.availableMissions;
      }
      
      throw error;
    }
  }

  // Check if player meets mission requirements
  checkRequirements(mission, player) {
    if (!mission.requirements || mission.requirements.length === 0) {
      return true;
    }

    return mission.requirements.every(req => {
      switch (req.type) {
        case 'level':
          return player.level >= req.value;
        case 'health':
          return player.health >= req.value;
        case 'money':
          return player.money >= req.value;
        case 'item':
          // Check inventory for required item
          const inventory = player.inventory || [];
          const item = inventory.find(i => i.id === req.id);
          return item && item.quantity >= req.quantity;
        default:
          console.warn(`Unknown requirement type: ${req.type}`);
          return true;
      }
    });
  }

  // Accept a mission (start it)
  async accept(missionId) {
    try {
      const response = await api.startMission(missionId);
      const missionRun = response.missionRun;
      
      // Create local state for timers
      const activeRun = {
        runId: missionRun.id,
        missionId: missionRun.mission.id,
        mission: missionRun.mission,
        startedAt: new Date(missionRun.startedAt).getTime(),
        endsAt: new Date(missionRun.completesAt).getTime(),
        status: 'active'
      };

      this.state.activeRun = activeRun;
      this.saveState();

      // Set timer in game store
      const playerStore = window.Alpine?.store('app');
      if (playerStore) {
        playerStore.setTimer('mission', activeRun.endsAt, () => {
          this.completeMission(activeRun.runId);
        });
      }

      console.log(`Mission accepted: ${missionRun.mission.name} (${activeRun.runId})`);
      return activeRun;
      
    } catch (error) {
      console.error('Failed to accept mission:', error);
      throw new Error(error.data?.message || 'No se pudo aceptar la misión');
    }
  }

  // Get currently active mission run
  async getActiveRun() {
    try {
      const response = await api.getActiveMission();
      
      if (!response.mission) {
        // No active mission on server, clear local state
        if (this.state.activeRun) {
          this.state.activeRun = null;
          this.saveState();
        }
        return null;
      }

      const mission = response.mission;
      
      // Update local state with server data
      const activeRun = {
        runId: mission.id,
        missionId: mission.missionId,
        mission: {
          id: mission.missionId,
          name: mission.name,
          description: mission.description,
          difficulty: mission.difficulty,
          duration: mission.duration
        },
        startedAt: new Date(mission.startedAt).getTime(),
        endsAt: new Date(mission.completesAt).getTime(),
        status: mission.isComplete ? 'completed' : 'active'
      };

      // Only save and emit if state actually changed
      const stateChanged = !this.state.activeRun || 
        this.state.activeRun.runId !== activeRun.runId ||
        this.state.activeRun.status !== activeRun.status;

      this.state.activeRun = activeRun;
      
      if (stateChanged) {
        this.saveState();
      }

      // Set timer if mission is still active
      if (!mission.isComplete) {
        const playerStore = window.Alpine?.store('app');
        if (playerStore) {
          playerStore.setTimer('mission', activeRun.endsAt, () => {
            this.completeMission(activeRun.runId);
          });
        }
      }

      return activeRun;
      
    } catch (error) {
      console.error('Failed to fetch active mission:', error);
      
      // Return cached state if available
      if (this.state.activeRun) {
        console.warn('Using cached active mission data');
        return this.state.activeRun;
      }
      
      return null;
    }
  }

  // Complete mission (called by timer or manually)
  async completeMission(runId) {
    if (!this.state.activeRun || this.state.activeRun.runId !== runId) {
      console.warn(`Mission run ${runId} not found or not active`);
      return;
    }

    // Mark as completed locally first
    this.state.activeRun.status = 'completed';
    this.state.activeRun.completedAt = Date.now();
    this.saveState();

    console.log(`Mission completed: ${this.state.activeRun.mission.name}`);

    // Clear timer
    const playerStore = window.Alpine?.store('app');
    if (playerStore) {
      playerStore.clearTimer('mission');
    }

    return this.state.activeRun;
  }

  // Claim mission rewards
  async claim() {
    try {
      const response = await api.completeMission();
      
      // Update player state will be handled by the player store sync
      const playerStore = window.Alpine?.store('app');
      if (playerStore) {
        // Refresh player data to get new stats/coins
        await playerStore.refreshPlayer();
        
        // Show rewards notification
        const rewards = response.rewards;
        let rewardText = '';
        
        if (rewards.coins > 0) {
          rewardText += `+${rewards.coins} monedas`;
        }
        
        if (rewards.items && rewards.items.length > 0) {
          const itemText = rewards.items.map(item => `${item.quantity}x ${item.itemId}`).join(', ');
          rewardText += rewardText ? `, ${itemText}` : itemText;
        }
        
        playerStore.addNotification(`¡Misión completada! ${rewardText}`, 'success');
      }

      // Move to completed runs
      const completedRun = {
        ...this.state.activeRun,
        claimedAt: Date.now(),
        success: true,
        actualReward: response.rewards
      };

      this.state.completedRuns.push(completedRun);
      this.state.activeRun = null;

      // Limit completed runs history
      if (this.state.completedRuns.length > 50) {
        this.state.completedRuns = this.state.completedRuns.slice(-50);
      }

      this.saveState();

      console.log(`Mission claimed: ${response.message}`);
      return { success: true, reward: response.rewards, completedRun };
      
    } catch (error) {
      console.error('Failed to claim mission:', error);
      throw new Error(error.data?.message || 'No se pudo reclamar la recompensa');
    }
  }

  // Sync with server (called periodically)
  async syncWithServer() {
    try {
      const activeRun = await this.getActiveRun();
      return activeRun;
    } catch (error) {
      console.error('Failed to sync missions with server:', error);
      return null;
    }
  }

  // Cancel active mission
  cancel() {
    if (!this.state.activeRun) {
      throw new Error('No hay misión activa para cancelar.');
    }

    const playerStore = window.Alpine?.store('app');
    if (playerStore) {
      playerStore.clearTimer('mission');
    }

    this.state.activeRun = null;
    this.saveState();

    console.log('Mission cancelled');
  }

  // Helper methods
  findMissionById(missionId) {
    for (const cityId in missionsData.cities) {
      const city = missionsData.cities[cityId];
      const mission = city.missions.find(m => m.id === missionId);
      if (mission) {
        return mission;
      }
    }
    return null;
  }

  generateRunId() {
    return 'run_' + Date.now() + '_' + Math.random().toString(36).substring(2);
  }

  // Get mission tier info
  getTierInfo(tier) {
    return missionsData.tiers[tier] || { color: '#gray', label: tier, icon: '❓' };
  }

  // Get completed missions count
  getCompletedCount() {
    return this.state.completedRuns.length;
  }

  // Get completed missions by city
  getCompletedByCity(cityId) {
    return this.state.completedRuns.filter(run => 
      run.mission.cityId === cityId
    );
  }

  // Reset all mission data (for testing)
  reset() {
    this.state = { ...defaultState };
    this.saveState();
    
    // Clear any active timers
    const playerStore = window.Alpine?.store('app');
    if (playerStore) {
      playerStore.clearTimer('mission');
    }
    
    console.log('Missions data reset');
  }

  // Get statistics
  getStats() {
    const total = this.state.completedRuns.length;
    const successful = this.state.completedRuns.filter(run => run.success).length;
    const totalCoins = this.state.completedRuns.reduce((sum, run) => 
      sum + (run.actualReward?.coins || 0), 0
    );

    return {
      totalCompleted: total,
      successfulRuns: successful,
      successRate: total > 0 ? Math.round((successful / total) * 100) : 0,
      totalCoinsEarned: totalCoins
    };
  }
}

// Export singleton instance
export const missionsService = new MissionsService();

// Export class for testing
export { MissionsService };